"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";

import { ExternalLink } from "@/components/ui/externalLink";

import {
    ClearPathLendingLogo,
    Facebook,
    Twitter,
    Instagram,
    OpinionCorpLogo,
    ReviewRating,
    FooterImg,
} from "@assets/icons";

function Footer() {
    const pathname = usePathname();

    return (
        <footer className="bg-lightgray text-textSecondary font-lato text-sm lg:pl-[185px]">
            {/* Top Section */}
            <div className="border-b border-black pt-[50px] pb-[60px]">
                <div className="max-w-[1200px] mx-auto px-6 md:pl-24 md:pr-6">
                    {/*<div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-0">*/}
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-4 lg:gap-0">
                        {/* Column 1 */}
                        <div>
                            <div className="mb-[25px]">
                                <Link
                                    href="/"
                                    aria-label="Home"
                                    aria-current={pathname === "/" ? "page" : undefined}
                                >
                                    <Image
                                        src={ClearPathLendingLogo}
                                        alt=""
                                        aria-hidden="true"
                                        className="w-[200px] h-[72px] md:w-[155px] md:h-[55px] xl:w-[200px] xl:h-[72px]"
                                    />
                                </Link>
                            </div>
                            <div className="flex gap-[5px] justify-center md:justify-center pt-[20px]">
                                <ExternalLink
                                    href="https://www.facebook.com/profile.php?id=100057376392350"
                                    ariaLabel="Facebook (opens in a new tab)"
                                    rel="noopener noreferrer"
                                >
                                    <span className="sr-only">Facebook (opens in a new tab)</span>
                                    <Image src={Facebook} alt="" aria-hidden="true" className="w-10 h-10" />
                                </ExternalLink>

                                <ExternalLink
                                    href="https://twitter.com/ClearPathLend"
                                    ariaLabel="X / Twitter (opens in a new tab)"
                                    rel="noopener noreferrer"
                                >
                                    <span className="sr-only">X / Twitter (opens in a new tab)</span>
                                    <Image src={Twitter} alt="" aria-hidden="true" className="w-10 h-10" />
                                </ExternalLink>

                                <ExternalLink
                                    href="https://www.instagram.com/clearpathlending/"
                                    ariaLabel="Instagram (opens in a new tab)"
                                    rel="noopener noreferrer"
                                >
                                    <span className="sr-only">Instagram (opens in a new tab)</span>
                                    <Image src={Instagram} alt="" aria-hidden="true" className="w-10 h-10" />
                                </ExternalLink>
                            </div>
                        </div>

                        {/* Column 2 */}
                        <div>
                            <h2 className="text-[16px] font-semibold text-darkBlue uppercase mb-[7px] lg:text-[16px] ">
                                Contact Info
                            </h2>
                            <ul className="list-none pl-2 lg:w-[140px] lg:pl-0">
                                <li className="flex items-center gap-2 mb-2 text-black xl:w-[270px]">
                                    <i className="fa fa-map-marker text-blue !text-xl" aria-hidden="true"></i>
                                    <span>
                                        15615 ALTON PKWY #300
                                        <br />
                                        IRVINE, CA 92618
                                    </span>
                                </li>
                                <li className="flex items-center gap-2 mb-2 text-black">
                                    <i className="fa fa-phone text-blue !text-xl" aria-hidden="true"></i>
                                    <a
                                        href="tel:8558665363"
                                        aria-label="Call (855) 866-5363"
                                        className="hover:text-blue text-[16px]"
                                    >
                                        (855) 866-5363
                                    </a>
                                </li>
                                <li className="flex items-center gap-2 text-black xl:w-[270px]">
                                    <i className="fa fa-clock-o text-blue !text-xl" aria-hidden="true"></i>
                                    MON-FRI 7:00 AM to 7:00 PM
                                </li>
                            </ul>
                        </div>

                        {/* Column 3 */}
                        <div>
                            <h2 className="text-[16px] font-semibold text-darkBlue uppercase mb-[7px] lg:text-[16px]">
                                Compliance
                            </h2>
                            <ul className="list-none uppercase leading-[26px]">
                                <li>
                                    <Link
                                        href="/privacy"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Privacy
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/terms"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Terms
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/licenses"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Licenses
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/assumption"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Assumptions
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/accessibility"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Accessibility
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/triggerleads"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        TRIGGER LEADS
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        {/* Column 4 */}
                        <div>
                            <h2 className="text-[16px] font-semibold text-darkBlue uppercase mb-[7px] lg:text-[16px]">
                                Calculators
                            </h2>
                            <ul className="list-none uppercase leading-[26px]">
                                <li>
                                    <Link
                                        href="/payment-calculator"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Mortgage Payment Calculator
                                    </Link>
                                </li>
                                <li className={"xl:w-[280px]"}>
                                    <Link
                                        href="/refinance-calculator"
                                        className="text-black font-medium hover:text-blue transition ease-in-out xl:w-[279px]"
                                    >
                                        How Much Will I Save by Refinancing
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        href="/price-range-calculator"
                                        className="text-black font-medium hover:text-blue transition ease-in-out"
                                    >
                                        Max Monthly Payment Calculator
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Section */}
            <div className="bg-lightgray text-black py-4">
                <div className="max-w-[1200px] mx-auto px-6">
                    <div className="flex flex-wrap justify-center lg:justify-between items-center gap-4">
                        <div className="flex items-center gap-4">
                            <Image src={FooterImg} alt="Equal Housing Lender" />
                            <div className="flex flex-col text-2xs text-center lg:text-left">
                                <p>ClearPath Lending Inc. ©{new Date().getFullYear()}</p>
                                <p className="leading-relaxed">
                                    NMLS #936436 Equal Housing Lender |{" "}
                                    <ExternalLink
                                        href="https://www.nmlsconsumeraccess.org"
                                        className="hover:text-blue"
                                        rel="noopener noreferrer"
                                    >
                                        www.nmlsconsumeraccess.org<span className="sr-only"> (opens in a new tab)</span>
                                    </ExternalLink>
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-[1rem]">
                            <ExternalLink
                                href="https://clearpath-lending.opinion-corp.com/review.html"
                                className="flex-shrink-0"
                                ariaLabel="Opinion Corp reviews (opens in a new tab)"
                                rel="noopener noreferrer"
                            >
                                <span className="sr-only">Opinion Corp reviews (opens in a new tab)</span>
                                <Image src={OpinionCorpLogo} alt="" aria-hidden="true" className="h-[40px] w-auto" />
                            </ExternalLink>

                            <ExternalLink
                                href="https://www.consumeraffairs.com/go/cta?url=https://www.consumeraffairs.com/finance/clearpath-lending.html&element_label=brands_app_widget_click"
                                className="flex-shrink-0"
                                ariaLabel="ConsumerAffairs reviews (opens in a new tab)"
                                rel="noopener noreferrer"
                            >
                                <span className="sr-only">ConsumerAffairs reviews (opens in a new tab)</span>
                                <Image src={ReviewRating} alt="" aria-hidden="true" className="h-[48px] w-auto" />
                            </ExternalLink>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default Footer;
